import { HospitalsTable } from 'components/DataExpansionComponents/Hospitals/HospitalsTable/HospitalsTable'
import { K12Table } from 'components/DataExpansionComponents/K12/K12Table/K12Table'
import { UniversitiesTable } from 'components/DataExpansionComponents/Universities/UniversitiesTable/UniversitiesTable'
import { NursingHomesTable } from 'components/DataExpansionComponents/NursingHomes/NursingHomeTable/NursingHomesTable'

import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageInnerTabsContainer,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { Tabs } from 'components/FbUI/Tabs'
import LoadingScreen from 'components/Layout/LoadingScreen'
import { NotFoundContent } from '../not-found-content'
import { ChainsTable } from 'components/Tables/ChainsTable/ChainsTable'
import { CompaniesTable } from 'components/Tables/CompaniesTable/CompaniesTable'
import { ContactRequestsTable } from 'components/Tables/ContactRequestsTable/ContactRequestsTable'
import { ContactsTable } from 'components/Tables/ContactsTable/ContactsTable'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import { DealsTable } from 'components/Tables/DealsTable/DealsTable'
import {
  CHAINS_TABLE_KEY,
  COLLEGE_AND_UNIVERSITY_TABLE_KEY,
  CONTACT_REQUEST_JOB_TABLE_KEY,
  CONTACTS_TABLE_KEY,
  CONVENIENCE_TABLE_KEY,
  DEAL_TABLE_KEY,
  getCampaignTableKey,
  getCompanyTableKey,
  HOSPITALS_TABLE_KEY,
  K12_TABLE_KEY,
  LODGING_TABLE_KEY,
  NURSING_HOMES_TABLE_KEY,
} from 'constants/tableQueryKeys'
import { Campaign } from 'models/campaign'
import {
  ALLOWED_CONTACT_REQUEST_TYPES,
  Category,
  CompanyTypeSlug,
  CompanyTypeSlugWithFBData,
  SubCategory,
} from 'models/companies'
import { FilterIdentifier } from 'models/saved_view'
import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import apiService from 'services/api'
import { useGetCampaignById } from 'features/campaigns/campaigns_api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useNavbarStore } from '../../../components/Layout/navbar-store' // Adjust the path as needed
import useDocumentTitle from '../../../components/useDocumentTitle'
import { ContactRequestJobFromCampaignModal } from '../../../components/Modals/ContactRequestJobFromCampaignModal'
import { ChainsSummary } from 'models/summaries'
import { StandardSummaryResponse } from 'models/paginated-response'
import { LodgingTable } from 'components/DataExpansionComponents/GooglePlaces/Lodging/LodgingTable/LodgingTable'
import { ConvenienceTable } from 'components/DataExpansionComponents/GooglePlaces/Convenience/ConvenienceTable/ConvenienceTable'

const TABLE_CONTAINER_HEIGHT = 'calc(var(--base-page-table-height) - 102px)'

export function SingleCampaignPage() {
  const featureFlag = useFeatureFlagService()
  const navigate = useNavigate()
  const params = useParams<{ campaign_id: string; tab?: string }>()
  const campaignId = params.campaign_id
  const selectedTab = params.tab ?? 'opportunities'
  const store = useNavbarStore()
  const api = apiService()
  const { data: campaign, isLoading: campaignsLoading } = useGetCampaignById(
    parseInt(campaignId ?? '-1')
  )
  const { data: categories, isFetching: categoriesLoading } =
    api.useGetCompanyCategories()

  const [chainsSummary, setChainsSummary] = useState<ChainsSummary>({
    isLoading: true,
    total_ltv: 0,
    count: 0,
    places_count: 0,
    avg_pounds_per_year: 0,
    avg_total_ltv: 0,
  })
  const [k12Count, setK12Count] = useState<StandardSummaryResponse>({
    count: 0,
    alt_count: 0,
  })
  const [hospitalCount, setHospitalCount] = useState<StandardSummaryResponse>({
    count: 0,
    alt_count: 0,
  })
  const [universityCount, setUniversityCount] =
    useState<StandardSummaryResponse>({
      count: 0,
      alt_count: 0,
    })
  const [nursingHomeCount, setNursingHomeCount] =
    useState<StandardSummaryResponse>({
      count: 0,
      alt_count: 0,
    })
  const [lodgingCount, setLodgingCount] = useState<StandardSummaryResponse>({
    count: 0,
    alt_count: 0,
  })
  const [convenienceCount, setConvenienceCount] =
    useState<StandardSummaryResponse>({
      count: 0,
      alt_count: 0,
    })
  const [companiesCount, setCompaniesCount] = useState<StandardSummaryResponse>(
    {
      count: 0,
      alt_count: 0,
    }
  )

  const hasContactRequests = useMemo(() => {
    if (!campaign) return false
    // Multi-type campaigns (type is null) always show contact requests
    if (!campaign.type) return true
    return ALLOWED_CONTACT_REQUEST_TYPES.includes(campaign.type.slug as any)
  }, [campaign])

  const campaignTypes = useMemo(() => {
    const types: {
      slug: string
      sub_category: SubCategory
      category: Category
    }[] = []
    categories?.forEach((it) => {
      it.sub_categories.forEach((s) => {
        types.push({
          slug: s.slug,
          sub_category: s,
          category: it,
        })
      })
    })
    return types
  }, [categories])

  const type = campaignTypes?.find((it) => it?.slug === campaign?.type?.slug)

  // Move the totalOpportunitiesCount calculation to the component level
  const totalOpportunitiesCount = useMemo(() => {
    if (!campaign) return 0

    switch (campaign.type?.slug) {
      case 'restaurants-bars':
        return chainsSummary.count
      case 'education-cu':
        return universityCount.count
      case 'education-k-12':
        return k12Count.count
      case 'healthcare-hospitals':
        return hospitalCount.count
      case 'healthcare-nursinghomes':
        return nursingHomeCount.count
      case 'lodging-hospitality':
        return lodgingCount.count
      case 'convenience':
        return convenienceCount.count
      default:
        return companiesCount.count ?? 0
    }
  }, [
    campaign,
    chainsSummary.count,
    universityCount.count,
    k12Count.count,
    hospitalCount.count,
    nursingHomeCount.count,
    lodgingCount.count,
    convenienceCount.count,
    companiesCount,
  ])

  useDocumentTitle(`Campaign - ${campaign?.name} - First Bite`)

  // Redirect to opportunities tab if trying to access contact_requests tab for unsupported campaigns
  React.useEffect(() => {
    if (
      selectedTab === 'contact_requests' &&
      !hasContactRequests &&
      campaignId
    ) {
      navigate(`/campaigns/${campaignId}/targets`, { replace: true })
    }
  }, [selectedTab, hasContactRequests, campaignId, navigate])

  if (campaignsLoading) {
    return <LoadingScreen />
  }

  if (!campaign) {
    return <NotFoundContent />
  }
  if (!featureFlag.isLoading && !featureFlag.enableCampaigns) {
    navigate('/')
    return null
  }

  const handleTabChange = (tab: string) => {
    const url = `/campaigns/${campaignId}/${tab}`
    navigate(url)
    if (campaignId) {
      store.setLastCampaignTabVisited(campaignId, url)
    }
  }

  const getCampaignTable = () => {
    if (categoriesLoading || (!type && campaign.type)) {
      return <LoadingScreen />
    }

    // Explicitly making this CompanyTypeSlugWithFBData type, to REQUIRE that we have a table for each of these types
    const tableBySlugType: Record<
      CompanyTypeSlugWithFBData,
      React.JSX.Element
    > = {
      'restaurants-bars': (
        <ChainsTable
          setChainsSummary={setChainsSummary}
          hideCreateCompanyButton={true}
          filterIdentifierModifier={
            `_CAMPAIGN_${campaign.id}` as FilterIdentifier
          }
          useReports={
            featureFlag.enableCampaignDenormalizedStats ? false : undefined
          }
          defaultColumnVisibility={{
            chain: true,
            count: true,
            menu: false,
            menu_length: false,
            menu_matches: false,
            total_ltv: false,
            brand_ltv: false,
            revenue_ltv: false,
            one_year_total_value: false,
            taro: false,
            tabo: false,
            pounds_per_year: false,
            note_count: true,
            contact_count: true,
            deal_count: true,
            sales_stages: true,
            account_owners: true,
            tags: true,
            distributors: true,
            cuisine_50: false,
            expense_category: false,
            rating: false,
            reviews_count: false,
            velocity_group: false,
            domain: false,
            instagram_url: false,
            instagram_followers: false,
            instagram_following: false,
            instagram_posts: false,
            instagram_percentile: false,
            instagram_text_percentile: false,
            ingredients_matrix: false,
            country_summary: false,
            state_summary: false,
            city_summary: false,
            zip_summary: false,
            median_hhi: false,
            pop_density: false,
            hh_gt100k: false,
            reputation_data: false,
            michelin_stars_count: false,
            hq_state2: false,
            hq_city2: false,
          }}
          tableKey={getCampaignTableKey(CHAINS_TABLE_KEY, campaign.id)}
          baseFilters={{
            main_campaign: campaign.id,
            ...(featureFlag.enableCampaignDenormalizedStats && {
              view_mode: 'campaign',
            }),
          }}
        />
      ),
      'education-cu': (
        <UniversitiesTable
          setTotalRowsCount={setUniversityCount}
          hideCreateCompanyButton={true}
          defaultColumnVisibility={{
            institution_name: true,
            address: false,
            city: false,
            state_abbr: false,
            zipcode: false,
            domain: false,
            dormitory_capacity: false,
            meal_plan_provided: false,
            total_enrollment: false,
            total_men_enrollment: false,
            total_women_enrollment: false,
            food_management_company: false,
            food_management_company_parent: false,
            estimated_meals_per_day: false,
            carnegie_classification_2021_size_setting_size: false,
            carnegie_classification_2021_size_setting_year: false,
            carnegie_classification_2021_size_setting_student_composition: false,
            campaigns: false,
            deal_count: true,
            contact_count: true,
            note_count: true,
            sales_stages: true,
            account_owner: true,
            taglist: true,
            distributors: true,
            google_place_url: false,
          }}
          filterIdentifierModifier={
            `_CAMPAIGN_${campaign.id}` as FilterIdentifier
          }
          tableKey={getCampaignTableKey(
            COLLEGE_AND_UNIVERSITY_TABLE_KEY,
            campaign.id
          )}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
      'education-k-12': (
        <K12Table
          setTotalRowsCount={setK12Count}
          hideCreateCompanyButton={true}
          filterIdentifierModifier={
            `_CAMPAIGN_${campaign.id}` as FilterIdentifier
          }
          tableKey={getCampaignTableKey(K12_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
          defaultColumnVisibility={{
            k12district__lea_name: true,
            k12district__address: false,
            k12district__zipcode: false,
            k12district__lcity: false,
            k12district__lstate: false,
            k12district__county: false,
            k12district__locale: false,
            k12district__operational_schools: false,
            k12district__gslo: false,
            k12district__gshi: false,
            k12district__student_count: false,
            k12district__teachers_count: false,
            k12district__phone: false,
            campaigns: false,
            sales_stages: true,
            account_owners: true,
            taglist: true,
            contact_count: true,
            note_count: true,
            distributors: true,
            deal_count: true,
            k12district__revenue: false,
            k12district__reduced_lunch: false,
            k12district__free_lunch: false,
            k12district__estimated_meals_per_day: false,
            k12district__food_service_expenditures: false,
            k12district__total_supplemented_lunch: false,
            k12district__total_supplemented_lunch_perc: false,
            k12district__domain: false,
            k12district__google_place_url: false,
            k12district__uncategorized_lunch: false,
            k12district__free_lunch_perc: false,
            k12district__reduced_lunch_perc: false,
            k12district__uncategorized_lunch_perc: false,
          }}
        />
      ),
      'healthcare-hospitals': (
        <HospitalsTable
          setTotalRowsCount={setHospitalCount}
          hideCreateCompanyButton={true}
          defaultColumnVisibility={{
            provider_number: true,
            hospital_name: true,
            street_addr: false,
            po_box: false,
            city: false,
            state: false,
            zip_code: false,
            year: false,
            total_hospital_days: false,
            total_hospital_beds: false,
            year_2: false,
            cafeteria: false,
            dietary: false,
            dietary_under_contract: false,
            campaigns: false,
            sales_stages: true,
            account_owners: true,
            taglist: true,
            contact_count: true,
            note_count: true,
            distributors: true,
            deal_count: true,
          }}
          filterIdentifierModifier={
            `_CAMPAIGN_${campaign.id}` as FilterIdentifier
          }
          tableKey={getCampaignTableKey(HOSPITALS_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
      'healthcare-nursinghomes': (
        <NursingHomesTable
          setTotalRowsCount={setNursingHomeCount}
          hideCreateCompanyButton={true}
          defaultColumnVisibility={{
            provider_name: true,
            address: false,
          }}
          filterIdentifierModifier={
            `_CAMPAIGN_${campaign.id}` as FilterIdentifier
          }
          tableKey={getCampaignTableKey(NURSING_HOMES_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
      'lodging-hospitality': (
        <LodgingTable
          setTotalRowsCount={setLodgingCount}
          hideCreateCompanyButton={true}
          defaultColumnVisibility={{
            provider_name: true,
            address: false,
          }}
          filterIdentifierModifier={
            `_CAMPAIGN_${campaign.id}` as FilterIdentifier
          }
          tableKey={getCampaignTableKey(LODGING_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
      convenience: (
        <ConvenienceTable
          setTotalRowsCount={setConvenienceCount}
          hideCreateCompanyButton={true}
          defaultColumnVisibility={{
            provider_name: true,
            address: false,
          }}
          filterIdentifierModifier={
            `_CAMPAIGN_${campaign.id}` as FilterIdentifier
          }
          tableKey={getCampaignTableKey(CONVENIENCE_TABLE_KEY, campaign.id)}
          baseFilters={{ main_campaign: campaign.id }}
        />
      ),
    }

    const tableForUnmatchedCompanyType = (
      <CompaniesTable
        setTotalRowsCount={setCompaniesCount}
        subCategory={type?.sub_category}
        category={type?.category}
        filterIdentifierModifier={
          `_CAMPAIGN_${campaign.id}` as FilterIdentifier
        }
        tableKey={getCampaignTableKey(
          type
            ? getCompanyTableKey(type.slug as CompanyTypeSlug)
            : 'multi-type-targets',
          campaign.id
        )}
        baseFilters={{ campaigns: campaign.id }}
      />
    )

    let oppsTable
    if (
      campaign.type?.slug &&
      Object.keys(tableBySlugType).includes(campaign.type.slug)
    ) {
      oppsTable =
        tableBySlugType[campaign.type.slug as CompanyTypeSlugWithFBData]
    } else {
      oppsTable = tableForUnmatchedCompanyType
    }

    return (
      <TableContainer
        totalOpportunitiesCount={totalOpportunitiesCount}
        campaign={campaign}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        hasContactRequests={hasContactRequests}
        key={`campaign-${campaign.id}`}
        oppsTable={oppsTable}
        useDenormalizedStats={featureFlag.enableCampaignDenormalizedStats}
      />
    )
  }

  return (
    <FBPage>
      <FBPageHeader>
        <FBPageTitleRow className="w-auto">
          <FBPageTitle>
            <span style={{ fontWeight: 400 }}>Campaign - </span>
            {campaign.name}
          </FBPageTitle>
        </FBPageTitleRow>
        <div className="flex gap-3">
          <ContactRequestJobFromCampaignModal
            campaignId={campaign.id}
            isMultiType={!campaign.type}
            disabled={!hasContactRequests}
            disabledTooltipText={
              !hasContactRequests
                ? "Contact requests are not available for companies outside First Bite's database."
                : undefined
            }
          />
        </div>
      </FBPageHeader>
      {getCampaignTable()}
    </FBPage>
  )
}

function TableContainer(props: {
  oppsTable: React.JSX.Element
  campaign: Campaign
  selectedTab: string
  hasContactRequests: boolean
  onTabChange: (tab: string) => void
  totalOpportunitiesCount: number
  useDenormalizedStats?: boolean
}) {
  const [totalDealsCount, setTotalDealsCount] = useState<number>(0)
  const [totalContactsCount, setTotalContactsCount] = useState<number>(0)
  const [totalContactRequestsCount, setTotalContactRequestsCount] =
    useState<number>(0)

  const tabs = useMemo(() => {
    const tabs = [
      {
        title: (
          <span>
            Targets&nbsp;
            <i className="font-extralight not-italic">
              ({props.totalOpportunitiesCount})
            </i>
          </span>
        ),
        key: 'opportunities',
      },
      {
        title: (
          <span>
            Deals&nbsp;
            <i className="font-extralight not-italic">({totalDealsCount})</i>
          </span>
        ),
        key: 'deals',
      },
      {
        title: (
          <span>
            Contacts&nbsp;
            <i className="font-extralight not-italic">({totalContactsCount})</i>
          </span>
        ),
        key: 'contacts',
      },
    ]

    // Only add Contact Requests tab if the campaign supports contact requests
    if (props.hasContactRequests) {
      tabs.push({
        title: (
          <span>
            Contact Requests&nbsp;
            <i className="font-extralight not-italic">
              ({totalContactRequestsCount})
            </i>
          </span>
        ),
        key: 'contact_requests',
      })
    }

    return tabs
  }, [
    props.totalOpportunitiesCount,
    totalDealsCount,
    totalContactsCount,
    totalContactRequestsCount,
    props.hasContactRequests,
  ])

  return (
    <>
      <FBPageInnerTabsContainer>
        <Tabs
          tabs={tabs as any}
          selectedTab={props.selectedTab}
          setSelectedTab={props.onTabChange}
          tabStyle={{ width: '140px' }}
        />
      </FBPageInnerTabsContainer>
      <FBPageTableContainer
        style={{ height: TABLE_CONTAINER_HEIGHT }}
        hide={props.selectedTab !== 'opportunities'}
      >
        {props.oppsTable}
      </FBPageTableContainer>
      <FBPageTableContainer
        style={{ height: TABLE_CONTAINER_HEIGHT }}
        hide={props.selectedTab !== 'deals'}
      >
        <DealsTable
          useReports={props.useDenormalizedStats ? false : true}
          setTotalRowsCount={setTotalDealsCount}
          showCompanyColumn
          disableUrlPagination
          filterIdentifierModifier={`_CAMPAIGN_${props.campaign.id}`}
          defaultColumnVisibility={{ identifier: false }}
          baseFilters={{
            origin_campaign: props.campaign.id,
            ...(props.useDenormalizedStats && { view_mode: 'campaign' }),
          }}
          tableKey={getCampaignTableKey(DEAL_TABLE_KEY, props.campaign.id)}
        />
      </FBPageTableContainer>
      <FBPageTableContainer
        style={{ height: TABLE_CONTAINER_HEIGHT }}
        hide={props.selectedTab !== 'contacts'}
      >
        <ContactsTable
          setTotalRowsCount={setTotalContactsCount}
          disableUrlPagination
          campaignId={props.campaign.id}
          campaignProductId={props.campaign.product?.id ?? null}
          defaultColumnVisibility={{ requested_contact__campaign__name: false }}
          tableKey={getCampaignTableKey(CONTACTS_TABLE_KEY, props.campaign.id)}
          filterIdentifierModifier={`_CAMPAIGN_${props.campaign.id}`}
          baseFilters={{ campaign: props.campaign.id }}
        />
      </FBPageTableContainer>
      <TableLinkStyleProvider variant="plain">
        <StageColorProvider value="pipeline">
          <FBPageTableContainer
            style={{
              height: TABLE_CONTAINER_HEIGHT,
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              background: '#ffffff',
              overflow: 'hidden',
              boxShadow:
                '0px 1px 3px 0px rgba(16, 24, 40, 0.08), 0px 1px 2px -1px rgba(16, 24, 40, 0.04)',
            }}
            hide={props.selectedTab !== 'contact_requests'}
          >
            <ContactRequestsTable
              setTotalRowsCount={setTotalContactRequestsCount}
              tableKey={getCampaignTableKey(
                CONTACT_REQUEST_JOB_TABLE_KEY,
                props.campaign.id
              )}
              campaignId={props.campaign.id}
            />
          </FBPageTableContainer>
        </StageColorProvider>
      </TableLinkStyleProvider>
    </>
  )
}
